import type { UpsellItem } from '@bulknutrients/bulk-shared-lib/interfaces/ICart';
import { AddToCartStatus } from '@components/Button/AddToCart';
import {
  UIContext,
  type UIContextInterface,
} from '@components/Context/UIContext/UIContext';
import { CustomImage } from '@components/Media/Image/Image';
import { useAddItems } from '@hooks/cart/useAddItems';
import { log } from '@lib/logs';
import * as Toast from '@radix-ui/react-toast';
import { useContext, useState } from 'react';

export function ToastAlert({
  open,
  onOpenChange,
  message,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  message: string | undefined;
}) {
  return (
    <Toast.Provider duration={5000} swipeDirection="right">
      <Toast.Root
        open={open}
        onOpenChange={onOpenChange}
        className="rounded-2xl items-center bg-black p-4 text-white dark:bg-white dark:text-black shadow-toast
            data-[state=open]:animate-[toastSlideIn_300ms_cubic-bezier(0.16,1,0.3,1)]
            data-[state=closed]:animate-[toastSwipeOut_100ms_ease-in]
            data-[swipe=move]:translate-x-(--radix-toast-swipe-move-x)
            data-[swipe=cancel]:translate-x-0 data-[swipe=cancel]:transition-[transform_200ms_ease-out]
            data-[swipe=end]:animate-[toastSwipeOut_100ms_ease-out]  
            "
      >
        <Toast.Title className="italic text-lg text-center">
          Brilliant stuff!
        </Toast.Title>
        <Toast.Description className="text-center">{message}</Toast.Description>
      </Toast.Root>

      <Toast.Viewport className="fixed bottom-0 left-0 right-0 z-3 m-auto flex w-full max-w-[485px] list-none flex-col gap-2.5 p-6 outline-hidden" />
    </Toast.Provider>
  );
}

export function QuickCartUpsell({
  upsellItem,
  showUpsellBanner,
}: {
  upsellItem: UpsellItem | undefined;
  showUpsellBanner: boolean;
}) {
  const { setAddingToCartStatus } = useContext<UIContextInterface>(UIContext);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Add upsell item to cart
  const addItem = useAddItems({
    include: ['line_items.physical_items.options'],
  });
  const addToCart = async () => {
    setAddingToCartStatus(AddToCartStatus.Adding);
    try {
      if (!upsellItem) throw new Error('Upsell item is undefined');
      const { error } = await addItem({
        lineItems: [
          {
            productId: upsellItem.variant.productId,
            variantId: upsellItem.variant.variantId,
            quantity: 1,
          },
        ],
      });

      if (error) throw Error(error);

      setAddingToCartStatus(AddToCartStatus.Added);
      // show confirmation message after adding upsell item to cart
      setShowConfirmation(true);
    } catch (err) {
      setAddingToCartStatus(AddToCartStatus.Failed);
      log({ error: err, location: 'QuickCartUpsell addToCart' });
    }
  };

  // return empty div when upsell item does not exist
  if (!upsellItem) return null;

  return (
    <div>
      <ToastAlert
        open={showConfirmation}
        onOpenChange={setShowConfirmation}
        message={upsellItem?.message}
      />
      {showUpsellBanner && !showConfirmation && (
        <button
          type="button"
          onClick={addToCart}
          data-product-id={upsellItem.variant.productId}
          className="cart-upsell-item mx-0 w-full border-none bg-transparent p-0"
        >
          <CustomImage image={upsellItem.image} width={500} />
        </button>
      )}
    </div>
  );
}
